/**
 * Keeps MetaInfo queries fresh after every committed DECLARATION write —
 * including MCP declarations and Blueprint imports, which have no renderer
 * mutation callback of their own to invalidate for them.
 *
 * Derived-cache writes (`kind: 'cache'` — `jsonStructureCache` /
 * `enumValueCache`) are deliberately ignored here. The grids' passive
 * JSON-structure aggregator commits one every 5 s while a JSON table is on
 * screen, and refetching the whole MetaInfo document on those closes a loop:
 * the refetch hands the grid a NEW metaInfo object (fresh `updatedAt` /
 * `sampledAt`, so structural sharing cannot dedupe it), which churns the
 * `forcedJsonColumnIds` / `jsonColumnIdSet` memo identities in
 * `TableExplorerGrid`, re-runs the sample-push effect over the same page of
 * rows, re-marks the aggregate dirty and schedules the next flush — bounded
 * only by the aggregator's 1000-sample cap. The renderer surfaces that show
 * cached values (`useEnumValues`, `lookupCacheRefresh`, the MetaInfo manager's
 * own actions) invalidate for themselves at their own call sites, which is
 * where that responsibility sat before the commit-boundary broadcast existed.
 *
 * See Docs/MetaInfo.md → "Committed-write notifications".
 */

import { useEffect } from 'react';
import { useInvalidateMetaInfo } from '../../workspace/common/hooks/useMetaInfo';

export function useMetaInfoDeclareApplied(): void {
  const invalidate = useInvalidateMetaInfo();

  useEffect(() => {
    const cleanup = window.electronAPI?.metainfo?.onWritten(
      (connectionId, database, kind) => {
        if (kind !== 'declaration') return;
        void invalidate(connectionId, database);
      },
    );
    return () => {
      cleanup?.();
    };
  }, [invalidate]);
}
